/**
 * RayMarchingScene - Ray Marching渲染组件
 *
 * React Three Fiber组件，封装了基于Ray Marching技术的珠宝渲染器。
 * 实现了物理光学效果包括反射、折射、色散和多重散射。
 *
 * @component
 * @example
 * ```tsx
 * <RayMarchingScene />
 * ```
 *
 * 特性：
 * - 实时动画：自动旋转的水晶和环绕相机
 * - 响应式：自动适配画布尺寸变化
 * - 高性能：GPU并行计算，适合实时预览
 *
 * 核心渲染逻辑请参考：
 * - Fragment Shader: ./shaders/raymarching.frag
 * - Vertex Shader: ./shaders/raymarching.vert
 * - 参考实现: docs/reference/raymarching.glsl
 */

import { useRef, useMemo, useState, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import {
  ShaderMaterial,
  Mesh,
  Texture,
  CubeTexture,
  EquirectangularReflectionMapping,
} from "three";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { CubeTextureLoader } from "three";
import fragmentShaderBase from "./shaders/raymarching.frag";
import vertexShader from "./shaders/raymarching.vert";
import environmentShader from "./shaders/environment.glsl";

interface RayMarchingSceneProps {
  useEnvironmentMap?: boolean;
  selectedHDR?: string; // 选择的HDR文件名
  environmentIntensity?: number;
  environmentExposure?: number;
}

export function RayMarchingScene({
  useEnvironmentMap = false,
  selectedHDR = "brown_photostudio_02_2k.hdr",
  environmentIntensity = 2.0,
  environmentExposure = 1.0, // HDR默认曝光调整为1.0
}: RayMarchingSceneProps = {}) {
  const meshRef = useRef<Mesh>(null);
  const { size } = useThree();
  const [envMap, setEnvMap] = useState<Texture | null>(null);

  // 加载选择的HDR环境贴图
  useEffect(() => {
    if (!useEnvironmentMap) {
      setEnvMap(null);
      return;
    }

    const loader = new RGBELoader();
    // 根据选择的HDR文件加载
    loader.load(
      `/assets/textures/${selectedHDR}`,
      (texture) => {
        texture.mapping = EquirectangularReflectionMapping;
        console.log(
          `HDR environment map loaded successfully: ${selectedHDR}`,
          texture,
        );
        setEnvMap(texture);
      },
      undefined,
      (error) => {
        console.warn(`Failed to load environment HDR: ${selectedHDR}`, error);
      },
    );
  }, [useEnvironmentMap, selectedHDR]);

  // 创建shader材质，拼接环境光模块和主shader
  const material = useMemo(() => {
    // 将共享的环境光模块注入到fragment shader中
    const fragmentShader = environmentShader + "\n" + fragmentShaderBase;

    return new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        iTime: { value: 0.0 },
        iResolution: { value: [size.width, size.height] },
        iMouse: { value: [400, 300, 1.0, 1.0] },
        iChannel0: { value: null },
        // 环境贴图相关uniforms
        uEnvironmentMap: { value: envMap },
        uUseEnvironmentMap: { value: useEnvironmentMap && envMap !== null },
        uEnvironmentIntensity: { value: environmentIntensity },
        uEnvironmentExposure: { value: environmentExposure },
        // 相机控制uniforms
        uCameraPosition: { value: [0, 0, 7] },
        uCameraTarget: { value: [0, 0, 0] },
        uCameraUp: { value: [0, 1, 0] },
        // 透视投影参数
        uFov: { value: 50.0 }, // 与PBR模式一致的FOV
        uAspect: { value: size.width / size.height },
      },
    });
  }, [
    size.width,
    size.height,
    envMap,
    useEnvironmentMap,
    environmentIntensity,
    environmentExposure,
  ]);

  // 每帧更新shader uniforms
  useFrame((state) => {
    if (material.uniforms) {
      material.uniforms.iTime.value = state.clock.elapsedTime;
      material.uniforms.iResolution.value = [size.width, size.height];
      material.uniforms.iMouse.value = [
        state.pointer.x,
        state.pointer.y,
        1.0,
        1.0,
      ];
      // 更新环境贴图相关uniforms
      material.uniforms.uEnvironmentMap.value = envMap;
      material.uniforms.uUseEnvironmentMap.value =
        useEnvironmentMap && envMap !== null;
      material.uniforms.uEnvironmentIntensity.value = environmentIntensity;
      material.uniforms.uEnvironmentExposure.value = environmentExposure;

      // 更新相机uniforms - 从Three.js状态获取实时相机数据
      const camera = state.camera;
      material.uniforms.uCameraPosition.value = [
        camera.position.x,
        camera.position.y,
        camera.position.z,
      ];
      // OrbitControls的target就是相机看向的目标点
      const controls = state.controls as any;
      if (controls && controls.target) {
        material.uniforms.uCameraTarget.value = [
          controls.target.x,
          controls.target.y,
          controls.target.z,
        ];
      }
      material.uniforms.uCameraUp.value = [
        camera.up.x,
        camera.up.y,
        camera.up.z,
      ];

      // 更新透视投影参数
      material.uniforms.uFov.value = 50.0; // 与PBR模式一致
      material.uniforms.uAspect.value = size.width / size.height;
    }
  });

  // 渲染全屏四边形作为shader画布（裁剪空间坐标 -1 到 1）
  return (
    <mesh ref={meshRef} material={material}>
      <planeGeometry args={[2, 2]} />
    </mesh>
  );
}
